import { StyleSheet } from '@react-pdf/renderer';
import { StatusTagVariant } from '../consolidatedReportModel';

/** Usable width on A4 with 40pt horizontal padding. */
export const PDF_CONTENT_WIDTH = 515;
/** `.outerCard` horizontal padding (10 + 10). */
export const PDF_OUTER_CARD_PAD = 20;
/** Width available for condition/status columns inside the organ card. */
export const PDF_ROW_INNER_WIDTH = PDF_CONTENT_WIDTH - PDF_OUTER_CARD_PAD;
export const PDF_COL_GAP = 10;
export const PDF_COL_LEFT = Math.floor(PDF_ROW_INNER_WIDTH * 0.52);
export const PDF_COL_RIGHT = PDF_ROW_INNER_WIDTH - PDF_COL_LEFT - PDF_COL_GAP;
export const PDF_CONDITION_TEXT_WIDTH = PDF_COL_LEFT - PDF_COL_GAP;
export const PDF_SYSTEM_STATUS_WIDTH = 200;
export const PDF_SYSTEM_NOTES_WIDTH = PDF_CONTENT_WIDTH - PDF_SYSTEM_STATUS_WIDTH - 14;
/** Patient booking card: two equal columns with a divider. */
export const PDF_BOOKING_DIVIDER_GAP = 12;
export const PDF_BOOKING_KEY_WIDTH = 128;
export const PDF_BOOKING_COL_WIDTH = Math.floor(
  (PDF_CONTENT_WIDTH - PDF_BOOKING_DIVIDER_GAP) / 2
);
export const PDF_BOOKING_VALUE_WIDTH =
  PDF_BOOKING_COL_WIDTH - PDF_BOOKING_KEY_WIDTH;

export const colors = {
  text: '#122733',
  muted: '#6a6a6c',
  body: '#2e2e2e',
  cardBg: '#f7f7f9',
  systemBg: '#f5f5f7',
  white: '#ffffff',
  border: '#e2e2e5',
  rowBorder: '#cee4f3',
  outerCardBg: '#fbfcff',
  outerCardBorder: '#a1cce7',
  important: '#b13d3d',
  importantBg: '#eec8c8',
  critical: '#b13d3d',
  criticalBg: '#eec8c8',
  incidental: '#db8d21',
  incidentalBg: '#fadfb6',
  significant: '#db8d21',
  significantBg: '#fadfb6',
  inconclusive: '#856404',
  inconclusiveBg: '#fff3cd',
  noAbnormality: '#438f51',
  noAbnormalityBg: '#d0e5d4',
  noFindings: '#438f51',
  noFindingsBg: '#d0e5d4',
  notReported: '#6a6a6c',
  notReportedBg: '#e2e2e5',
  seen: '#0066cc',
  seenBg: '#b3d9ff',
  benign: '#0066cc',
  benignBg: '#b3d9ff',
  defaultTag: '#344054',
  defaultTagBg: '#e8eaef',
  chipPurple: '#5d59cc'
};

export const statusTagStyles: Record<
  StatusTagVariant,
  { backgroundColor: string; color: string }
> = {
  important: { backgroundColor: colors.importantBg, color: colors.important },
  critical: { backgroundColor: colors.criticalBg, color: colors.critical },
  incidental: { backgroundColor: colors.incidentalBg, color: colors.incidental },
  significant: { backgroundColor: colors.significantBg, color: colors.significant },
  inconclusive: { backgroundColor: colors.inconclusiveBg, color: colors.inconclusive },
  'no-abnormality': { backgroundColor: colors.noAbnormalityBg, color: colors.noAbnormality },
  'no-findings': { backgroundColor: colors.noFindingsBg, color: colors.noFindings },
  'not-reported': { backgroundColor: colors.notReportedBg, color: colors.notReported },
  seen: { backgroundColor: colors.seenBg, color: colors.seen },
  benign: { backgroundColor: colors.benignBg, color: colors.benign },
  default: { backgroundColor: colors.defaultTagBg, color: colors.defaultTag }
};

export const pdfStyles = StyleSheet.create({
  page: {
    paddingTop: 36,
    paddingBottom: 40,
    paddingHorizontal: 40,
    fontFamily: 'Helvetica',
    fontSize: 10,
    color: colors.text,
    lineHeight: 1.45
  },
  title: {
    fontSize: 18,
    fontWeight: 700,
    marginBottom: 16,
    color: colors.text
  },
  card: {
    backgroundColor: colors.cardBg,
    borderRadius: 10,
    padding: 14,
    marginBottom: 12
  },
  cardTitle: {
    fontSize: 13,
    fontWeight: 700,
    marginBottom: 10,
    color: colors.text
  },
  subheading: {
    fontSize: 11,
    fontWeight: 700,
    marginBottom: 6,
    color: colors.text
  },
  bodyText: {
    fontSize: 10,
    color: colors.text,
    lineHeight: 1.5
  },
  mutedText: {
    fontSize: 10,
    color: colors.muted,
    lineHeight: 1.5
  },
  fieldLabel: {
    fontSize: 9,
    fontWeight: 700,
    marginBottom: 4,
    color: colors.text
  },
  bookingKvRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 6
  },
  bookingKvKey: {
    width: PDF_BOOKING_KEY_WIDTH,
    fontSize: 10,
    fontWeight: 700,
    color: colors.text,
    lineHeight: 1.35,
    flexShrink: 0
  },
  bookingKvValue: {
    width: PDF_BOOKING_VALUE_WIDTH,
    fontSize: 10,
    fontWeight: 500,
    color: colors.text,
    lineHeight: 1.45
  },
  bookingColumns: {
    flexDirection: 'row',
    width: PDF_CONTENT_WIDTH
  },
  bookingCol: {
    width: PDF_BOOKING_COL_WIDTH,
    paddingRight: 6
  },
  bookingColRight: {
    width: PDF_BOOKING_COL_WIDTH,
    borderLeftWidth: 1,
    borderLeftColor: '#d5d5d8',
    paddingLeft: PDF_BOOKING_DIVIDER_GAP
  },
  notesStack: {
    flexDirection: 'column'
  },
  notesStackItem: {
    marginBottom: 10
  },
  filterBar: {
    backgroundColor: colors.white,
    borderRadius: 6,
    padding: 10,
    marginBottom: 14
  },
  filterSummaryRow: {
    flexDirection: 'row',
    marginBottom: 8
  },
  filterLabel: {
    width: 36,
    fontSize: 10,
    color: colors.muted,
    fontWeight: 500
  },
  filterValue: {
    width: PDF_CONTENT_WIDTH - 36,
    fontSize: 10,
    color: colors.body,
    fontWeight: 500,
    lineHeight: 1.45
  },
  chipRow: {
    flexDirection: 'row',
    flexWrap: 'wrap'
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 12,
    borderWidth: 1.5,
    borderColor: colors.important,
    paddingVertical: 4,
    paddingHorizontal: 8,
    marginRight: 6,
    marginBottom: 6
  },
  chipIncidental: {
    borderColor: colors.incidental
  },
  chipSeen: {
    borderColor: colors.chipPurple
  },
  chipSignificant: {
    borderColor: colors.significant
  },
  chipInconclusive: {
    borderColor: colors.inconclusive
  },
  chipBenign: {
    borderColor: colors.benign
  },
  chipCritical: {
    borderColor: colors.critical
  },
  chipCount: {
    width: 18,
    height: 18,
    borderRadius: 9,
    backgroundColor: colors.important,
    marginRight: 5,
    justifyContent: 'center',
    alignItems: 'center'
  },
  chipCountText: {
    color: colors.white,
    fontSize: 8,
    fontWeight: 700,
    textAlign: 'center'
  },
  chipCountIncidental: {
    backgroundColor: colors.incidental
  },
  chipCountSeen: {
    backgroundColor: colors.chipPurple
  },
  chipCountSignificant: {
    backgroundColor: colors.significant
  },
  chipCountInconclusive: {
    backgroundColor: colors.inconclusive
  },
  chipCountBenign: {
    backgroundColor: colors.benign
  },
  chipCountCritical: {
    backgroundColor: colors.critical
  },
  chipLabel: {
    fontSize: 9,
    maxWidth: 140,
    lineHeight: 1.3
  },
  systemShell: {
    backgroundColor: colors.systemBg,
    borderRadius: 12,
    padding: 14,
    marginBottom: 14
  },
  systemName: {
    fontSize: 18,
    fontWeight: 700,
    marginBottom: 12,
    color: colors.text
  },
  systemMetaRow: {
    flexDirection: 'row',
    marginBottom: 14,
    alignItems: 'flex-start'
  },
  systemMetaStatus: {
    width: PDF_SYSTEM_STATUS_WIDTH,
    paddingRight: 14
  },
  systemNotesBox: {
    width: PDF_SYSTEM_NOTES_WIDTH,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 6,
    padding: 8
  },
  systemNotesText: {
    fontSize: 10,
    color: colors.text,
    lineHeight: 1.45
  },
  organBlock: {
    marginBottom: 12
  },
  organTitle: {
    fontSize: 12,
    fontWeight: 700,
    marginBottom: 6,
    marginLeft: 4,
    color: colors.text
  },
  outerCard: {
    backgroundColor: colors.outerCardBg,
    borderWidth: 1,
    borderColor: colors.outerCardBorder,
    borderRadius: 6,
    paddingHorizontal: 10,
    paddingVertical: 8,
    width: PDF_CONTENT_WIDTH
  },
  rowHead: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: colors.rowBorder,
    paddingBottom: 5,
    marginBottom: 2,
    width: PDF_ROW_INNER_WIDTH
  },
  rowHeadCellLeft: {
    width: PDF_COL_LEFT,
    fontSize: 10,
    fontWeight: 700,
    paddingRight: PDF_COL_GAP
  },
  rowHeadCellRight: {
    width: PDF_COL_RIGHT,
    fontSize: 10,
    fontWeight: 700
  },
  innerRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    borderTopWidth: 1,
    borderTopColor: colors.rowBorder,
    paddingTop: 8,
    paddingBottom: 8,
    width: PDF_ROW_INNER_WIDTH
  },
  conditionCol: {
    width: PDF_COL_LEFT,
    paddingRight: PDF_COL_GAP,
    flexShrink: 1
  },
  conditionText: {
    fontSize: 10,
    fontWeight: 500,
    lineHeight: 1.45,
    width: PDF_CONDITION_TEXT_WIDTH
  },
  statusCol: {
    width: PDF_COL_RIGHT,
    flexShrink: 1
  },
  statusTagWrap: {
    alignSelf: 'flex-start',
    borderRadius: 6,
    paddingVertical: 5,
    paddingHorizontal: 8,
    marginBottom: 6,
    maxWidth: PDF_COL_RIGHT
  },
  systemStatusTagWrap: {
    alignSelf: 'flex-start',
    borderRadius: 6,
    paddingVertical: 5,
    paddingHorizontal: 8,
    marginTop: 4,
    maxWidth: PDF_SYSTEM_STATUS_WIDTH - 14
  },
  statusTagText: {
    fontSize: 9,
    fontWeight: 700,
    lineHeight: 1.35,
    width: PDF_COL_RIGHT - 16
  },
  systemStatusTagText: {
    fontSize: 9,
    fontWeight: 700,
    lineHeight: 1.35,
    width: PDF_SYSTEM_STATUS_WIDTH - 30
  },
  itemNote: {
    fontSize: 9,
    color: colors.text,
    lineHeight: 1.45,
    width: PDF_COL_RIGHT
  },
  descHint: {
    fontSize: 9,
    color: '#1fabff',
    lineHeight: 1.45,
    marginTop: 2,
    width: PDF_CONDITION_TEXT_WIDTH
  }
});
